const IMAGE_SIZE = 720;

const serializer = new XMLSerializer();

/**
 * Converts an SVG element to a downloadable PNG image URL.
 *
 * Renders the provided SVGSVGElement onto a canvas, exports it as a PNG image, and returns a temporary object URL pointing to the generated PNG.
 *
 * Note: The returned URL should be revoked with `URL.revokeObjectURL` after usage.
 *
 * @param {SVGSVGElement} svg - The SVG element to be converted to a downloadable PNG URL.
 * @param {number} size - The size of the image to export. Defaults to 720px.
 * @returns {Promise<string>} A promise that resolves with a PNG image object URL.
 */
export async function createSVGDownloadableURL(
  svg: SVGSVGElement,
  size: number = IMAGE_SIZE,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const svgString = serializer.serializeToString(svg);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });

    const url = URL.createObjectURL(svgBlob);
    const img = new Image();

    img.onload = () => {
      // Create canvas and draw image
      const canvas = document.createElement('canvas');

      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d')!;
      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);

      canvas.toBlob(blob => {
        if (blob) {
          const pngUrl = URL.createObjectURL(blob);
          resolve(pngUrl);
          setTimeout(() => URL.revokeObjectURL(pngUrl), 30_000);
        } else {
          reject(new Error('Failed to create PNG blob from SVG.'));
        }

        // Revoke the object URL to free-up memory
        URL.revokeObjectURL(url);
      }, 'image/png');
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Cannot render SVG to image.'));
    };

    img.src = url;
  });
}
